import {
	ChangeDetectionStrategy,
	Component,
	inject,
	Input,
	OnInit,
	Output,
	ViewChild,
	EventEmitter
} from "@angular/core";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import { Fiat, Validateable } from "@models";
import { StakingProcess, StakingActionType } from "@modules/staking/domain/entities/staking-process.model";
import { EMPTY, Observable, switchMap, tap } from "rxjs";
import { TradeAmountType } from "@modules/shared/domain/entities/trade-amount-type.model";
import { StakingHelperService } from "@modules/staking/domain/+state/utility/staking-helper.service";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import { UserJourney, AgreementBundle } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { convertObjectToArrayIfValTrue } from "@modules/trade/helpers/helper";
import {
	DYNAMIC_TERMS_TOKEN,
	TermsAndConditionsUpdatesComponent
} from "@modules/shared/terms-and-conditions/components/terms-and-conditions-updates.component";
import { Tradable } from "@models/interfaces/tradable";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { StakingMode } from "@gql/staking";

export interface IdynamicTermUpdatePayload {
	valid: boolean;
	agreedIds: string[];
	userJourney: UserJourney;
}

@Component({
	selector: "app-stake-summary",
	templateUrl: "./summary.component.html",
	styleUrls: ["./summary.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class SummaryComponent implements OnInit {
	private termsConditionsService: TermsAndConditionsService = inject(TermsAndConditionsService);

	@ViewChild(DYNAMIC_TERMS_TOKEN) dynamic_terms: TermsAndConditionsUpdatesComponent;
	@Input() isLockedEnabled: boolean;
	@Output() dynamicTermsUpdate = new EventEmitter<IdynamicTermUpdatePayload>();

	public stakingProcess$: Observable<StakingProcess> = EMPTY;
	public stakingSlider: StakingSummarySlider = new StakingSummarySlider();
	public defaultFiat: Fiat | null = null;

	public tradeAmountTypes: typeof TradeAmountType = TradeAmountType;
	public stakingType: typeof StakingActionType = StakingActionType;

	public tradable: Tradable = null;

	// angreement bundles
	private bundles: AgreementBundle[] = [];
	isDynamictermAndConditionActive$ = this.launchDarklyService.isActive$(LD_FLAG.TERMS_AND_CONDITIONS);
	bundles$: Observable<AgreementBundle[]> = this.stakingFacade.getStakingProcess$().pipe(
		switchMap((stakingProcess) => {
			//user journey
			const userJourney = this.userJourneyHelper(stakingProcess);

			// UserJourney[type === "Locked" ? `${action}${type}` : action];

			if (stakingProcess?.stakeable?.id) {
				this.tradable = this.tradableListService.getItemById(stakingProcess?.stakeable?.id);
			}
			//get bundles$
			return this.termsConditionsService.getBundlesRemote(userJourney).pipe(
				tap((bundles) => {
					this.bundles = bundles;
					if (bundles.length > 0) {
						//update the form use timeout to avoid ExpressionChangedAfterItHasBeenCheckedError
						//TODO: use OutPut to update the form on a parent component
						setTimeout(() => {
							// check validity of the dynamic form
							this.dynamic_terms.termsAndConditionsForm.statusChanges.subscribe((status) => {
								const valid = status === "VALID" && this.bundles.length > 0;
								const agreedIds = convertObjectToArrayIfValTrue(
									this.dynamic_terms?.termsAndConditionsForm?.value
								);
								this.dynamicTermsUpdate.emit({ valid, agreedIds, userJourney });
							});
						}, 100);
					} else {
						this.dynamicTermsUpdate.emit({
							valid: bundles.length === 0 && !this.termsConditionsService.hasBundlesError$$(),
							agreedIds: undefined,
							userJourney
						});
					}
				})
			);
		})
	);

	private userJourneyHelper(process: StakingProcess): UserJourney {
		const { type, stakeable } = process;
		if (!stakeable) {
			return null;
		}

		const { isLocked, stakingMode } = stakeable;
		const isStakingProcess = type === StakingActionType.STAKE; //means if process is staking or unstaking

		// Determine user journey based on staking mode and lock status

		switch (stakingMode) {
			case StakingMode.Earn:
				if (isLocked) {
					return isStakingProcess ? UserJourney.EarningLocked : UserJourney.ReleasingLocked;
				}
				return isStakingProcess ? UserJourney.Earning : UserJourney.Releasing;

			case StakingMode.Stake:
				if (isLocked) {
					return isStakingProcess ? UserJourney.StakingLocked : UserJourney.UnstakingLocked;
				}
				return isStakingProcess ? UserJourney.Staking : UserJourney.Unstaking;

			default:
				return null;
		}
	}

	public constructor(
		private readonly stakingFacade: StakingFacade,
		private readonly stakingHelperService: StakingHelperService,
		private readonly launchDarklyService: LaunchDarklyService,
		private readonly tradableListService: TradableListService
	) {}

	public ngOnInit(): void {
		this.stakingFacade.updateStakingProcess({ termsAccepted: this.stakingSlider.enabled });
		this.stakingProcess$ = this.stakingFacade.getStakingProcess$();
		this.defaultFiat = this.stakingHelperService.getFiat();
	}

	public toggleChange(terms: boolean): void {
		this.stakingFacade.updateStakingProcess({ termsAccepted: terms });
	}
}

class StakingSummarySlider extends Validateable {
	public enabled: boolean = false;
}
